﻿import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Box,
  Grid,
  Card,
} from "@mui/material";
import "./LifecyclePopup.css";
import { Close as CloseIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectAllWorkflows } from "@/features/homepage/homeSlice";
import { selectLifecycleDetails } from "@/features/homepage/workflowManagerSlice";
import { useTranslation } from "react-i18next";
import { SplitIcon, ConsolidateIcon } from "@/ImageRepo";

const LifeCyclePopup = ({ open, onClose, fromName, fromReference }) => {
  const { t } = useTranslation("homepage");

  const { WorkflowsCompleted, WorkflowsUpcoming } = useSelector(
    selectLifecycleDetails
  );

  const workflowMetadata = useSelector(selectAllWorkflows);

  const renderCard = (content, color, borderlineColor) => (
    <Box
      sx={{
        gap: "5px",
        marginBottom: "16px",
      }}
    >
      <Card
        className="lifecycle-workflow-card"
        style={{
          backgroundColor: color,
          border: `1px solid ${borderlineColor}`,
        }}
      >
        <Typography className="custom-typography">{content}</Typography>
      </Card>
    </Box>
  );

  const CompletedWorkflows = () => (
    <>
      {WorkflowsCompleted.map((workflow) => {
        let metaData = workflowMetadata.find(
          (x) => x.OperationId === workflow.OperationId
        );
        return (
          <Box className="completed-workflow-container">
            <Typography className="completed-workflow">
              <span className="workflow-square-completed"></span> Completed
              Workflow:{" "}
              <span className="workflow-content">
                {t(metaData.NameMessageId, metaData.OperationName)}
              </span>
            </Typography>

            <Grid
              container
              key={workflow.OperationId}
              className="lifecycle-workflow-grid"
            >
              <Grid
                item
                xs={6}
                sx={{ paddingRight: "5px", paddingTop: 0, paddingBottom: 0 }}
              >
                {renderCard(
                  <>
                    <span>{workflow.SourceReference}</span>{" "}
                    <strong>{metaData.ReceivingType}</strong>{" "}
                    <span>from {workflow.SourceConnectionName}</span>
                    {workflow.SourceReferenceType.length > 0 && (
                      <span>{" (" + workflow.SourceReferenceType + ")"} </span>
                    )}
                    <span>
                      {workflow.IsSplit && (
                        <SplitIcon className="split-icon-adjustment" />
                      )}
                    </span>
                    <span>
                      {workflow.IsSplit && (
                        <span className="split-label"> Split</span>
                      )}
                    </span>
                  </>,
                  "#eaf3ec",
                  "#d5e8da"
                )}
              </Grid>
              <Grid item xs={6}>
                {renderCard(
                  <>
                    <span>{workflow.TargetReference}</span>{" "}
                    <strong>Created & {metaData.TargetingType}</strong>{" "}
                    <span>to {workflow.TargetConnectionName}</span>
                    {workflow.TargetReferenceType.length > 0 && (
                      <span>{" (" + workflow.TargetReferenceType + ")"} </span>
                    )}
                    <span>
                      {workflow.IsConsolidate && (
                        <ConsolidateIcon className="consolidate-icon-adjustment" />
                      )}
                    </span>
                    <span>
                      {workflow.IsConsolidate && (
                        <span className="consolidate-label">Consolidate</span>
                      )}
                    </span>
                  </>,
                  "#eaf3ec",
                  "#d5e8da"
                )}
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </>
  );

  const UpcomingWorkflows = () => {
    const workflows = [
      ...WorkflowsUpcoming.filter((workflow) => workflow.Status === "next"),
      ...WorkflowsUpcoming.filter((workflow) => workflow.Status === "upcoming"),
    ];
    const getStyles = (status) => {
      return status === "next"
        ? {
            containerClass: "next-workflow-container",
            color: "#fcf7e9",
            borderlineColor: "#f8eed1",
          }
        : {
            containerClass: "upcoming-workflow-container",
            color: "#efe5f0",
            borderlineColor: "#e0cde3",
          };
    };

    return (
      <>
        {workflows.map((workflow) => {
          let metaData = workflowMetadata.find(
            (x) => x.OperationId === workflow.OperationId
          );
          const { containerClass, color, borderlineColor } = getStyles(
            workflow.Status
          );
          const workflowType =
            workflow.Status === "next" ? "Next Workflow" : "Upcoming Workflow";
          const squareClass =
            workflow.Status === "next"
              ? "workflow-square-next"
              : "workflow-square-upcoming";

          return (
            <Box className={containerClass} key={workflow.OperationId}>
              <Typography
                className={
                  workflow.Status === "next"
                    ? "next-workflow"
                    : "upcoming-workflow"
                }
              >
                <span className={squareClass}></span> {workflowType}:{" "}
                <span className="workflow-content">
                  {t(metaData.NameMessageId, metaData.OperationName)}
                </span>
              </Typography>
              <Grid container className="lifecycle-workflow-grid">
                <Grid item xs={6} sx={{ paddingRight: "5px" }}>
                  {renderCard(
                    <>
                      {workflow.SourceReference &&
                      workflow.SourceConnectionName ? (
                        <>
                          <span>{workflow.SourceReference}</span>{" "}
                          <strong>Received</strong>{" "}
                          <span>from {workflow.SourceConnectionName}</span>
                        </>
                      ) : (
                        <>
                          <strong>Waiting</strong>{" "}
                          <span>for {workflow.Source}</span>
                        </>
                      )}
                    </>,
                    color,
                    borderlineColor
                  )}
                </Grid>
                <Grid item xs={6}>
                  {renderCard(
                    <>
                      <span>{workflow.Target}</span> <strong>Pending</strong>{" "}
                      <span>to be Created & {metaData.TargetingType}</span>
                    </>,
                    color,
                    borderlineColor
                  )}
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <Dialog
      className="workflow-steps-popup"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: "lifecyclepopup-dialogPaper",
        sx: {
          zIndex: 1302,
          marginLeft: "auto",
          transform: "translateX(0)",
          // marginTop: "auto",
          marginTop: 30,
        },
      }}
      BackdropProps={{ sx: { zIndex: 1301, position: "absolute" } }}
    >
      <DialogTitle disableTypography className="lifecyclepopup-header">
        <Typography className="lifecyclepopup-heading">Lifecycle</Typography>
        <IconButton
          className="lifecyclepopup-closeIconButton"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="lifecyclepopup-dialogContent">
        <Typography
          variant="h5"
          gutterBottom
          className="lifecycle-headerContainer"
          style={{ fontSize: "18px" }}
        >
          <strong>{fromName}</strong> ({fromReference})
        </Typography>
        <Box className="lifecycle-workflowContainer">
          {WorkflowsCompleted?.length > 0 && <CompletedWorkflows />}
          {WorkflowsUpcoming?.length > 0 && <UpcomingWorkflows />}
        </Box>
      </DialogContent>
      <DialogContent className="lifecyclepopup-footer">
        <DialogActions>
          <Button
            className="closeButton"
            onClick={onClose}
            color="primary"
            variant="contained"
          >
            Close{" "}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default LifeCyclePopup;
